import { useEffect, useId, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import './NavDropdown.css';

/**
 * The "For the industry" menu.
 *
 * CLICK, NEVER HOVER, and that is the whole reason this is a component rather than a CSS
 * :hover trick. A hover-opened menu is unreachable on a touch device: there is no hover
 * state to enter, and the tap that would open it also fires the link underneath. Browsers
 * paper over this with an emulated hover on first tap, which then STICKS until you tap
 * somewhere else, so the menu hangs open over the page.
 *
 * A button with aria-expanded works identically with a mouse, a finger and a keyboard,
 * and it is the only one of the three that needs no special case.
 *
 * Closing is handled for every way out: Escape (returning focus to the trigger, so a
 * keyboard user is not dropped at the top of the document), a click anywhere outside, and
 * any navigation, since React Router changes the route without unmounting the header.
 */

export interface NavDropdownItem {
  to: string;
  label: string;
}

export function NavDropdown({
  label,
  shortLabel,
  items,
}: {
  label: string;
  /** Used where the full label will not fit. */
  shortLabel: string;
  items: NavDropdownItem[];
}) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuId = useId();
  const { pathname } = useLocation();

  // Navigating closes it. The header never unmounts, so without this the menu would
  // still be open on the page it just sent you to.
  useEffect(() => { setOpen(false); }, [pathname]);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      triggerRef.current?.focus();
    };

    // pointerdown rather than click, so a tap that lands outside dismisses the menu
    // without also activating whatever it landed on.
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div className="navdrop" ref={wrapperRef}>
      <button
        type="button"
        ref={triggerRef}
        className={open ? 'navdrop__trigger is-open' : 'navdrop__trigger'}
        aria-expanded={open}
        aria-controls={menuId}
        aria-haspopup="true"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="navdrop__label-full">{label}</span>
        <span className="navdrop__label-short">{shortLabel}</span>
        <svg
          className="navdrop__chevron"
          width="10"
          height="6"
          viewBox="0 0 10 6"
          aria-hidden="true"
          focusable="false"
        >
          <path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </button>

      {open && (
        <ul className="navdrop__menu" id={menuId}>
          {items.map((item) => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                className={({ isActive }) =>
                  isActive ? 'navdrop__item is-active' : 'navdrop__item'
                }
              >
                {item.label}
              </NavLink>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
